/**
 * Shared scheduling utilities
 * @module utils/scheduler
 */

export function getCurrentETString(): string {
  const now = new Date();
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(now);

  const get = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((part) => part.type === type)?.value ?? '';
  const pad = (value: string, length: number): string => value.padStart(length, '0');

  const year = pad(get('year'), 4);
  const month = pad(get('month'), 2);
  const day = pad(get('day'), 2);
  const minute = pad(get('minute'), 2);

  // Normalize hour to 24h. Two engine quirks to absorb:
  //  - some return "24" for midnight instead of "00"  -> % 24 maps it to 0
  //  - some ignore hour12:false and emit a 12h hour + am/pm dayPeriod
  let hour = parseInt(get('hour'), 10) % 24;
  const period = get('dayPeriod').toLowerCase();
  if (period.includes('p') && hour < 12) hour += 12;
  if (period.includes('a') && hour === 12) hour = 0;

  return `${year}-${month}-${day}T${pad(String(hour), 2)}:${minute}`;
}

const DATETIME_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/;

/**
 * Returns true if the current ET datetime falls within the given window.
 * An empty/missing bound is treated as open (no restriction on that side).
 * An invalid string for a bound is also treated as open (permissive).
 *
 * @param {string|null|undefined} startDatetime - ISO-like `YYYY-MM-DDTHH:MM` in ET, or empty/null
 * @param {string|null|undefined} endDatetime   - ISO-like `YYYY-MM-DDTHH:MM` in ET, or empty/null
 * @returns {boolean}
 */
export function isWithinSchedule(
  startDatetime: string | null | undefined,
  endDatetime: string | null | undefined,
): boolean {
  const current = getCurrentETString();
  const start = startDatetime ? startDatetime.trim() : '';
  const end = endDatetime ? endDatetime.trim() : '';

  if (DATETIME_RE.test(start) && current < start) return false;
  if (DATETIME_RE.test(end) && current > end) return false;
  return true;
}
